import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import { getInitials, getRandomColor } from '../../utils/UserCustomDisplay';
import PaginationControls from '../../utils/PaginationControls';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

const FormUsersList = ({ 
  apiBaseUrl,
  selectedUsers, 
  onSelectUser, 
  ownerEmail = null 
}) => {
  // Users data
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  
  // Pagination and search states
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  
  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');

  const navigate = useNavigate();

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Fetch users on component mount and when pagination or search changes
  useEffect(() => {
    fetchUsers();
  }, [page, size, search, ownerEmail]);

  // Fetch users from API
  const fetchUsers = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading users');
    
    try {
      // Create request body with pagination and search parameters
      const requestBody = {
        page: page,
        size: size,
        search: search || undefined,
        owner_email: ownerEmail || undefined
      };
      
      const response = await axios.post(
        `${apiBaseUrl}/users-list`,
        requestBody,
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load users');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setUsers(data.data || []);
      setTotal(data.total || 0);
      
    } catch (error) {
      console.error('Error fetching users:', error);
      setPopupErrorMessage(error.message || 'Failed to load users');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };
  
  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(1); // Reset to first page when search changes
  };
  
  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  // Handle user selection
  const handleUserSelection = (userId) => {
    if (onSelectUser) {
      onSelectUser(userId);
    }
  };
  
  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };
  
  return (
    <div className="ev-card-3">
      <div className="mb-2 d-flex position-relative" >
        <div className="col-7">
          <input
            type="text"
            className="form-control search-input ms-0"
             
            placeholder="Search users"
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyPress={handleSearchKeyPress}
          />
          <button 
          type="button"
            className="ev-btn-lightblue-fill-round search-bar-button" 
            onClick={handleSearch}
          >
            <i className="bi bi-search"></i>
          </button>        
        </div>
        <div className=' d-flex align-items-center mt-2 ms-3'>
            <button 
              className="ev-btn-light-fill ev-btn-xsmall" 
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            
            <span className="ev-small-p-dark ms-2">
              Page {page} of {Math.ceil(total / size) || 1}
            </span>
            
            <button 
              className="ev-btn-light-fill ev-btn-xsmall ms-2" 
              disabled={page >= Math.ceil(total / size)}
              onClick={() => setPage(page + 1)}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
        <div className="d-flex align-items-center ms-2">
           <PaginationControls 
          initialSize={size}
          onSizeChange={(newSize) => {
            setSize(newSize);
            setPage(1);
          }}
        />
        </div>
      </div>
      
      <div className="scrollable-element vh-20">
        {users.length === 0 ? (
          <p className="ev-small-p-blue text-center mt-2">No users found</p>
        ) : (
          users.map(user => (
            <div key={user.user_id} className="form-check mb-2 ms-1">
              <input
                className="form-check-input"
                type="checkbox"
                id={`user-${user.user_id}`}
                checked={selectedUsers.includes(user.user_id)}
                onChange={() => handleUserSelection(user.user_id)}
              />
                <label className="form-check-label d-flex align-items-center" htmlFor={`user-${user.user_id}`}>
                  <div 
                    className="user-circle user-circle--small d-flex align-items-center justify-content-center me-2 ms-2"
                    style={{ 
                      backgroundColor: getRandomColor(user.email),
                    }}
                    title={user.email}
                  >
                    {getInitials(user.email)} 
                  </div>
                  <p className="mb-0 ev-small-p-dark">
                    {user.email}
                  </p>
                </label>
            </div>
          ))
        )}
      </div>
      
      <div className="mt-2">
        <p className="ev-small-p-dark mb-0">Selected: {selectedUsers.length} users</p>
      </div>
      
      {/* Popups */}

      {isProcessingPopupVisible && createPortal(
          <ProcessingPopup message={processingMessage} />,
          document.body
      )}
      
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
    </div>
  );
};

export default FormUsersList;